import * as workspaceApi from "../backendapi/workspaceApi.js";
import { getWorkspacePlan, isPaidPlanNotTrial } from "../scripts/routes/workspace/utils";
import { isFeatureAvailable, Feature } from "@functions/featureAvailability";
import { projectsLowestCommonFolderId } from "../scripts/projectsLowestCommonFolder.ts";

const projectGroupsByWorkspace = {}; // how about we DONT store this on window :-)
let currentGroupId = null;
// TODO move all the global state of workspaces/usages + project Groups, Datasets to this module here

export async function loadProjectGroupsForWorkspace(workspaceId) {
    const cacheKey = `rf_projectGroups_${workspaceId}`;
    const currentTime = Date.now();
    //only use cache for the non-current workspaces so that we always get fresh data for the current workspace (Better UX)
    if (window.currentWorkspace !== workspaceId) {
        const cachedData = localStorage.getItem(cacheKey);

        if (cachedData) {
            const { data, expiry } = JSON.parse(cachedData);
            if (currentTime < expiry) {
                projectGroupsByWorkspace[workspaceId] = data;
                return;
            } else {
                localStorage.removeItem(cacheKey);
            }
        }
    }

    const data = await workspaceApi.getProjectGroupsForWorkspace(workspaceId);

    projectGroupsByWorkspace[workspaceId] = data;

    const expiry = currentTime + 5 * 60 * 1000; // 5 minute TTL
    localStorage.setItem(cacheKey, JSON.stringify({ data, expiry }));
}

export function hasFolderBilling(usage) {
    usage = usage || window.usage;
    return usage?.features?.folderBilling;
}

export function hasRBACAddon(usage) {
    usage = usage || window.usage;
    return isFeatureAvailable(Feature.RoleBasedAccessControl, usage);
}

export function hasLocalCommercialLicense() {
    const plan = getWorkspacePlan(window?.usage);
    if (["enterprise", "growth-ubp"].includes(plan)) {
        return true;
    }
    return false;
}

export function hasDedicatedDeployments() {
    const plan = getWorkspacePlan(window?.usage);
    if (["basic", "growth", "growth-ubp", "enterprise"].includes(plan)) {
        return true;
    }

    return isPaidPlanNotTrial(window?.usage);
}

export function hasProductionDedicatedDeployments() {
    const plan = getWorkspacePlan(window?.usage);
    if (["basic", "growth", "growth-ubp", "enterprise"].includes(plan)) {
        return true;
    }
    return false;
}

export function hasAutoscalingDedicatedDeployments() {
    const plan = getWorkspacePlan(window?.usage);
    if (["growth", "growth-ubp", "enterprise"].includes(plan)) {
        return true;
    }
    return false;
}

export function canUpgradeToDedicatedDeployments() {
    if (window?.usage?.features?.creditSystem === true) {
        return true;
    }

    return false;
}

export function hasAccessToProjectGroup(groupId, workspaceId, access = "any") {
    //TODO maybe move this to permissions.js?
    const projectGroups = projectGroupsByWorkspace[workspaceId];
    if (groupId && projectGroups) {
        const groupForThisProject = projectGroups.find((pg) => pg.id == groupId);
        if (groupForThisProject) {
            const prop = {
                any: "userHasAnyAccess",
                read: "userHasReadAccess",
                write: "userHasWriteAccess"
            }[access];
            return groupForThisProject[prop];
        }
    }
    return true;
}

export function getProjectGroupsOfWorkspace(workspaceId) {
    //TODO do we need to go as far as to return a COPY
    //so that folks don't inadvertedly mutate our state?
    const projectGroups = projectGroupsByWorkspace[workspaceId] || [];
    return projectGroups.sort((a, b) => a.name.localeCompare(b.name));
}

export function setCurrentGroupId(groupId) {
    currentGroupId = groupId;
}

export function getCurrentGroupId() {
    return currentGroupId;
}

export function getCurrentGroup() {
    return currentGroupId ? _findGroup(currentGroupId) : null;
}

export function _findGroup(groupId) {
    if (!window.currentWorkspace) {
        return null;
    }
    return projectGroupsByWorkspace[window.currentWorkspace]?.find((pg) => pg.id == groupId);
}

export async function getWorkspaceProjects() {
    // these are loaded / cached in app/src/scripts/setupFirebaseListeners.js
    // at page load time and guaranteed to be set for the current workspace
    // (at least in app context?!)

    const workspaceId = window.currentWorkspace;
    const projects = window.listenerCache[workspaceId];

    //only return ones user has access to via project groups
    return projects?.filter((p) => hasAccessToProjectGroup(p.group, workspaceId)) || [];
}

export function getProjectsLowestCommonFolder({ projectIds }) {
    const workspaceId = window.currentWorkspace;
    const folderBillingEnabled = hasFolderBilling();
    if (!folderBillingEnabled) {
        return null;
    }

    const workspaceProjects = window.listenerCache[workspaceId]; // NOTE: don't use getWorkspaceProjects() helper b/c it filters out projects by user permissions
    const filteredProjects =
        workspaceProjects?.filter((project) => projectIds.includes(project.id)) || [];
    const folderPaths = filteredProjects.map((project) => project.folderPath || []);
    return projectsLowestCommonFolderId(folderPaths);
}

//return project IDs visible to the current user, but only if the user is restricted (some project groups are not visible)
//returns undefined if the user is not restricted (admin or superuser)
export function getVisibleProjectsForRestrictedUser() {
    const workspaceId = window.currentWorkspace;
    const projectGroups = getProjectGroupsOfWorkspace(workspaceId);
    if (!projectGroups || projectGroups.every((group) => group.userHasAnyAccess)) {
        return undefined;
    }
    return projectGroups
        .filter((group) => group.userHasAnyAccess)
        .map((group) => group.projects)
        .flat();
}

export async function loadMyWorkspaces() {
    const workspaces = await workspaceApi.loadMyWorkspaces();

    window.workspacesById = workspaces.reduce((acc, ws) => {
        acc[ws.id] = { ...window.workspacesById[ws.id], ...ws };

        return acc;
    }, {});

    return workspaces;
}

export function userWorkspacesById() {
    return window.workspacesById || {};
}

export function goToWorkspace({ id = undefined, url = undefined, skipSetup = undefined }) {
    let newUrl = url ? `/${url}` : `/workspaces/${id}`;

    if (skipSetup) {
        newUrl += "?skipSetup=true";
    }

    window.location.href = newUrl;
}

export async function updateUserRoleInWorkspace(workspaceId, userIdToChange, role) {
    await workspaceApi.updateUserRoleInWorkspace(workspaceId, userIdToChange, role);
}

export async function removeUserFromWorkspace(workspaceId, userIdToRemove) {
    await workspaceApi.removeUserFromWorkspace(workspaceId, userIdToRemove);
}

export async function syncPrepaidInCentsWithUsage(workspaceId) {
    await workspaceApi.syncPrepaidInCentsWithUsage(workspaceId);
}

export async function usageCycleAuditLog(workspaceId, cycleStartSeconds, skipCache) {
    const result = await workspaceApi.usageCycleAuditLog({
        workspaceId,
        cycleStartSeconds,
        skipCache
    });
    return result;
}

export function getAllChildGroupIds(groupId, workspaceId) {
    const projectGroups = projectGroupsByWorkspace[workspaceId] || [];
    const groupHierarchy = new Map();
    projectGroups.forEach((group) => {
        if (group.parent_id) {
            if (!groupHierarchy.has(group.parent_id)) {
                groupHierarchy.set(group.parent_id, []);
            }
            groupHierarchy.get(group.parent_id).push(group.id);
        }
    });

    const childIds = groupHierarchy.get(groupId) || [];
    let allChildIds = [...childIds];
    childIds.forEach((childId) => {
        allChildIds = allChildIds.concat(getAllChildGroupIds(childId, workspaceId));
    });
    return allChildIds;
}

export default {
    hasRBACAddon,
    loadProjectGroupsForWorkspace,
    hasAccessToProjectGroup,
    getProjectGroupsOfWorkspace,
    setCurrentGroupId,
    getCurrentGroupId,
    getCurrentGroup,
    getWorkspaceProjects,
    loadMyWorkspaces,
    userWorkspacesById,
    goToWorkspace,
    hasDedicatedDeployments,
    hasProductionDedicatedDeployments,
    hasAutoscalingDedicatedDeployments,
    canUpgradeToDedicatedDeployments,
    updateUserRoleInWorkspace,
    removeUserFromWorkspace,
    syncPrepaidInCentsWithUsage,
    usageCycleAuditLog,
    getAllChildGroupIds,
    getProjectsLowestCommonFolder
};
